import {
  AlertTriangle,
  Archive,
  Download,
  ExternalLink,
  MoreHorizontal,
  Rocket,
  Trash2,
} from "lucide-react";

import { ClientSelect } from "@/components/client-select";
import { SortableTableHead, type SortDir } from "@/components/sortable-table-head";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { ProjectSummary } from "@/lib/api";
import { formatDate, formatDateTime, formatRelativeTime } from "@/lib/format";
import { getEffectiveStatus, getResourceRemovalState } from "@/lib/project-status";
import { cn, safeHttpUrl } from "@/lib/utils";

import { archiveDownloadUrl } from "./project-archive-dialog";

const STALE_AFTER_DAYS = 60;

// Orange rather than the amber `warning` badge variant, so a half torn down
// project is not mistaken at a glance for a merely "Partially completed" one.
const PARTLY_REMOVED_BADGE =
  "border-orange-300 bg-orange-100 text-orange-900 dark:border-orange-800 dark:bg-orange-950/60 dark:text-orange-200";

function isStale(dateStr: string | null): boolean {
  if (!dateStr) {
    return false;
  }
  const ageMs = Date.now() - new Date(dateStr).getTime();
  return ageMs > STALE_AFTER_DAYS * 24 * 60 * 60 * 1000;
}

/**
 * Whether a project's stored login rests on a check made inside the sweep
 * window, rather than on whatever we last managed to learn.
 *
 * The page's freshness line is a max across projects, so it cannot see this: a
 * project whose database has 5xx'd for six weeks keeps the login it had before
 * that, its `last_login_checked_at` stops advancing, and today's sweep of the
 * other forty-four rows still reports "checked just now". Everything this
 * column asserts about such a row is six weeks old.
 */
function hasFreshLoginEvidence(checkedAt: string | null, windowMs: number): boolean {
  if (!checkedAt) {
    return false;
  }
  const ms = new Date(checkedAt).getTime();
  return !Number.isNaN(ms) && Date.now() - ms <= windowMs;
}

interface ProjectTableProps {
  projects: ProjectSummary[];
  sortBy: string | null;
  sortDir: SortDir;
  onSort: (column: string) => void;
  onDelete: (project: ProjectSummary) => void;
  /** Dump the project's data to JSON and tear down its Supabase/Railway resources. */
  onArchive: (project: ProjectSummary) => void;
  onSetClient: (projectId: number, clientId: number | null) => void;
  /** Start provisioning the project's production environment. */
  onPromoteToProd: (project: ProjectSummary) => void;
  /** Project ids whose promotion request is still in flight locally. */
  promotingId: number | null;
  /**
   * How old a project's own `last_login_checked_at` may be before its login
   * value counts as unverified. Shared with the page's sweep window, so the
   * column and the freshness line cannot disagree about what "current" means.
   */
  loginCheckStaleMs: number;
}

export function ProjectTable({
  projects,
  sortBy,
  sortDir,
  onSort,
  onDelete,
  onArchive,
  onSetClient,
  onPromoteToProd,
  promotingId,
  loginCheckStaleMs,
}: ProjectTableProps) {
  if (projects.length === 0) {
    return <div className="text-muted py-12 text-center text-sm">No projects found.</div>;
  }

  const sortProps = { sortBy, sortDir, onSort };

  return (
    // `table-fixed` plus a width on every header is what makes the `truncate`
    // classes below fire: left to size themselves, the cells are all
    // `whitespace-nowrap` and grow to fit the longest email on the page.
    // Widths live on the header row only, so head and body cannot drift apart.
    // The floor stops the percentages squeezing a column under its content on
    // a narrow screen; below it the table's own scroller takes over, as before.
    <Table className="min-w-[56rem] table-fixed">
      <TableHeader>
        <TableRow>
          <SortableTableHead
            column="project_name"
            label="Project"
            className="w-[22%]"
            {...sortProps}
          />
          <SortableTableHead column="owner_name" label="Owner" className="w-[20%]" {...sortProps} />
          <TableHead className="w-[14%]">Client</TableHead>
          <SortableTableHead column="status" label="Status" className="w-[18%]" {...sortProps} />
          <SortableTableHead
            column="last_login_at"
            label="Last login"
            className="w-[13%]"
            {...sortProps}
          />
          {/* The least urgent column, so it is the one that goes on a narrow
              screen. Hidden on the body cell too, or the rows misalign. */}
          <SortableTableHead
            column="created_at"
            label="Created"
            className="hidden w-[9%] lg:table-cell"
            {...sortProps}
          />
          <TableHead className="w-10" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {projects.map((project) => {
          const status = getEffectiveStatus(project);
          // Takes precedence over the status badge: this table exists to spot
          // teardown drift, and resources left billing outrank a scaffold that
          // merely finished with a Railway error.
          const removal = getResourceRemovalState(project);
          // A null `last_login_checked_at` means we never managed to ask this
          // project's database, not that nobody ever signed in. The two must
          // not render alike: on this page "Never" reads as "unused, safe to
          // archive", and archiving cannot be undone.
          const loginCheckedAt = project.last_login_checked_at ?? null;
          const loginEvidenceFresh = hasFreshLoginEvidence(loginCheckedAt, loginCheckStaleMs);
          // No sign-in for the threshold is what marks a project abandoned. A
          // project nobody ever signed into has no timestamp to age, so the
          // clock starts at its creation date: a project scaffolded yesterday
          // is not abandoned, one nobody opened in six months plainly is.
          //
          // Gated on the evidence, not just on the value: amber is read as
          // "abandoned, archive it", so it is only ever shown for a login we
          // verified inside the sweep window. A row we could not reach keeps
          // its old value and loses the highlight, which invites a second look
          // instead of inviting an irreversible archive.
          const stale = loginEvidenceFresh && isStale(project.last_login_at ?? project.created_at);
          // `shut_down` is the authoritative "deliberately torn down" flag;
          // `archived_at` only ever supplies the date to render.
          const shutDown = project.shut_down;
          const archivedAt = project.archived_at;
          // `has_archive` is independent of `shut_down`: a teardown can store
          // the dump and then fail before marking the project, leaving it
          // looking live with its data already captured. That is precisely when
          // the download matters most, so it gates on the dump existing.
          const hasArchive = project.has_archive === true;
          // The backend refuses to archive a promoted project on either entry
          // point: archiving only the dev half would leave the prod resources
          // billing under an "Archived" badge. Factored out so a third entry
          // point inherits the guard instead of forgetting it, which is the
          // omission that produced this bug on both sides of the stack.
          // `prod_status` matters as much as the URLs: a promotion that failed
          // writes no URLs while its rollback suppresses delete failures, so it
          // can leave prod resources billing with both columns null, and one
          // still provisioning is a live race. The backend refuses both, so
          // offering the action here would only ever reach a dead end.
          const hasProd =
            !!project.prod_supabase_dashboard_url ||
            !!project.prod_railway_dashboard_url ||
            project.prod_status === "provisioning" ||
            project.prod_status === "failed";
          // A stored dump on a project that was never marked shut down makes
          // archiving a resumable "finish the job" rather than a destructive
          // first run: the backend skips the dump and the Supabase delete and
          // completes the teardown.
          const canResume = !shutDown && hasArchive && !hasProd;
          // Nothing left to tear down once the Supabase project is gone, and
          // archiving twice would re-dump an empty database over a good dump.
          const canArchive =
            !shutDown && !hasArchive && !hasProd && !!project.supabase_dashboard_url;
          // Promotion is only meaningful once a project finished scaffolding,
          // and must not be offered twice for the same environment.
          const prodStatus = project.prod_status;
          const canPromote = project.status === "completed" && !prodStatus;
          const promoting = prodStatus === "provisioning" || promotingId === project.id;
          const prodAppUrl = safeHttpUrl(project.prod_app_url);
          const prodRailwayUrl = safeHttpUrl(project.prod_railway_dashboard_url);
          const prodSupabaseUrl = safeHttpUrl(project.prod_supabase_dashboard_url);
          const githubUrl = safeHttpUrl(project.github_url);
          const appUrl = safeHttpUrl(project.app_url);
          const supabaseUrl = safeHttpUrl(project.supabase_dashboard_url);
          const railwayUrl = safeHttpUrl(project.railway_dashboard_url);
          return (
            <TableRow
              key={project.id}
              className={cn(
                stale && !shutDown && "bg-amber-50/50 dark:bg-amber-950/20",
                removal && "bg-orange-50/70 dark:bg-orange-950/25",
              )}
            >
              <TableCell>
                <div className="min-w-0">
                  <p className="text-foreground truncate text-sm font-medium">
                    {project.project_name}
                  </p>
                  <p className="text-muted truncate text-xs">{project.project_slug}</p>
                </div>
              </TableCell>
              <TableCell>
                <div className="min-w-0">
                  <p className="text-foreground truncate text-sm">
                    {project.owner_name || project.owner_email?.split("@")[0] || "Unknown"}
                  </p>
                  <p className="text-muted truncate text-xs">{project.owner_email ?? "No email"}</p>
                </div>
              </TableCell>
              <TableCell>
                <ClientSelect
                  value={project.client_id ?? null}
                  onChange={(clientId) => onSetClient(project.id, clientId)}
                  className="h-8 w-full max-w-[11rem] min-w-[8rem]"
                />
              </TableCell>
              {/* The only cell that wraps: its three "Gone / Still up / Setup
                  failed" lines have to read in full, and under `table-fixed`
                  a nowrap cell would spill them over the next column. */}
              <TableCell className="whitespace-normal">
                {removal ? (
                  <div className="min-w-0">
                    <Badge variant="warning" className={PARTLY_REMOVED_BADGE}>
                      <AlertTriangle />
                      Partly removed
                    </Badge>
                    {/* Spelled out rather than hidden behind a tooltip: an admin
                        needs to know what is still billing without hovering. */}
                    <p className="mt-1 text-xs text-orange-800 dark:text-orange-300">
                      Gone: {removal.removed.join(", ")}
                    </p>
                    <p className="text-muted text-xs">Still up: {removal.live.join(", ")}</p>
                    {removal.failed.length > 0 && (
                      <p className="text-muted text-xs">
                        Setup failed: {removal.failed.join(", ")}
                      </p>
                    )}
                    {/* An archive whose Railway teardown failed lands here. Say
                        so, or the row reads as drift of unknown origin. */}
                    {shutDown && (
                      <p className="text-muted text-xs">
                        Archived{archivedAt ? ` ${formatDate(archivedAt)}` : ""}
                      </p>
                    )}
                  </div>
                ) : shutDown ? (
                  <div className="min-w-0">
                    {/* Label and variant come from getEffectiveStatus so this
                        row reads the same as the owner's own project card. */}
                    <Badge variant={status.variant}>
                      <Archive />
                      {status.label}
                    </Badge>
                    {archivedAt && (
                      <p className="text-muted mt-1 text-xs">{formatDate(archivedAt)}</p>
                    )}
                  </div>
                ) : (
                  <Badge variant={status.variant}>{status.label}</Badge>
                )}
                {prodStatus && (
                  <p
                    className={cn(
                      "mt-1 text-xs",
                      prodStatus === "failed"
                        ? "text-destructive"
                        : prodStatus === "ready"
                          ? "text-green-700 dark:text-green-400"
                          : "text-muted",
                    )}
                    title={project.prod_error_message ?? undefined}
                  >
                    {prodStatus === "ready"
                      ? `prod live on ${project.prod_branch ?? "prod"}`
                      : prodStatus === "provisioning"
                        ? "prod provisioning..."
                        : "prod failed"}
                  </p>
                )}
              </TableCell>
              <TableCell
                className={cn(
                  "text-sm",
                  stale && !shutDown
                    ? "font-medium text-amber-700 dark:text-amber-400"
                    : "text-muted",
                )}
                // The exact moment behind the value, for an admin deciding
                // whether to trust it, without widening the column.
                title={loginCheckedAt ? `Checked ${formatDateTime(loginCheckedAt)}` : undefined}
              >
                {shutDown ? (
                  // An archived project's database is gone, so this value is
                  // frozen at the last sweep that could still reach it. Shown
                  // as a plain date with the qualifier spelled out: a relative
                  // "2 months ago" would keep ageing as if it were live data.
                  // An archive we never checked says so rather than claiming
                  // "Never", which would be the same unverified claim.
                  <div className="min-w-0">
                    <p className={cn("truncate", !loginCheckedAt && "italic")}>
                      {!loginCheckedAt
                        ? "Not checked"
                        : project.last_login_at
                          ? formatDate(project.last_login_at)
                          : "Never"}
                    </p>
                    <p className="text-muted truncate text-xs">before archiving</p>
                  </div>
                ) : !loginEvidenceFresh ? (
                  // Never asked and asked too long ago are the same claim: we
                  // do not know. They share one quiet, never amber treatment
                  // so neither can be read as a verified "nobody uses this".
                  // The stale row keeps its value as subordinate detail, since
                  // a real timestamp with its age attached is worth more to
                  // whoever has to decide than no timestamp at all.
                  <div className="min-w-0 italic">
                    <p className="truncate">
                      {loginCheckedAt
                        ? formatRelativeTime(project.last_login_at ?? null)
                        : "Not checked"}
                    </p>
                    {loginCheckedAt && (
                      <p className="text-muted truncate text-xs">
                        last checked {formatRelativeTime(loginCheckedAt).toLowerCase()}
                      </p>
                    )}
                  </div>
                ) : (
                  // `formatRelativeTime` renders a null as "Never", which now
                  // means what it says: we asked inside the sweep window, and
                  // nobody has ever signed in. The field is optional in the
                  // schema, so a missing one collapses to null here.
                  <p className="truncate">{formatRelativeTime(project.last_login_at ?? null)}</p>
                )}
              </TableCell>
              <TableCell className="text-muted hidden text-sm lg:table-cell">
                {formatDate(project.created_at)}
              </TableCell>
              <TableCell>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="size-8">
                      <MoreHorizontal className="size-4" />
                      <span className="sr-only">Project actions</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {githubUrl && (
                      <DropdownMenuItem asChild>
                        <a href={githubUrl} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="mr-2 size-4" /> Open GitHub
                        </a>
                      </DropdownMenuItem>
                    )}
                    {appUrl && (
                      <DropdownMenuItem asChild>
                        <a href={appUrl} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="mr-2 size-4" /> Open application
                        </a>
                      </DropdownMenuItem>
                    )}
                    {supabaseUrl && (
                      <DropdownMenuItem asChild>
                        <a href={supabaseUrl} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="mr-2 size-4" /> Supabase dashboard
                        </a>
                      </DropdownMenuItem>
                    )}
                    {railwayUrl && (
                      <DropdownMenuItem asChild>
                        <a href={railwayUrl} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="mr-2 size-4" /> Railway dashboard
                        </a>
                      </DropdownMenuItem>
                    )}
                    {prodAppUrl && (
                      <DropdownMenuItem asChild>
                        <a href={prodAppUrl} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="mr-2 size-4" /> Open prod application
                        </a>
                      </DropdownMenuItem>
                    )}
                    {prodSupabaseUrl && (
                      <DropdownMenuItem asChild>
                        <a href={prodSupabaseUrl} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="mr-2 size-4" /> Prod Supabase dashboard
                        </a>
                      </DropdownMenuItem>
                    )}
                    {prodRailwayUrl && (
                      <DropdownMenuItem asChild>
                        <a href={prodRailwayUrl} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="mr-2 size-4" /> Prod Railway dashboard
                        </a>
                      </DropdownMenuItem>
                    )}
                    {(canPromote || prodStatus === "failed") && (
                      <>
                        <DropdownMenuSeparator />
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <DropdownMenuItem
                              disabled={promoting}
                              onSelect={(e) => e.preventDefault()}
                            >
                              <Rocket className="mr-2 size-4" />
                              {promoting
                                ? "Provisioning prod..."
                                : prodStatus === "failed"
                                  ? "Retry bump to prod"
                                  : "Bump to prod"}
                            </DropdownMenuItem>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>
                                Bump {project.project_name} to prod?
                              </AlertDialogTitle>
                              <AlertDialogDescription>
                                This creates a <code>prod</code> branch and provisions a separate
                                Supabase project and Railway project that deploy from it. Both are
                                billable. The project keeps deploying <code>main</code> to its
                                existing resources, which stay untouched and become its dev
                                environment. Provisioning takes a few minutes.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => onPromoteToProd(project)}>
                                Bump to prod
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </>
                    )}
                    {hasArchive && (
                      <DropdownMenuItem asChild>
                        {/* Plain link: the admin session cookie authenticates it
                            and the browser saves the attachment. */}
                        <a href={archiveDownloadUrl(project.id)} download>
                          <Download className="mr-2 size-4" /> Download archive dump
                        </a>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    {(canArchive || canResume) && (
                      <DropdownMenuItem onClick={() => onArchive(project)}>
                        <Archive className="mr-2 size-4" />{" "}
                        {canResume ? "Finish archiving" : "Archive project"}
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem variant="destructive" onClick={() => onDelete(project)}>
                      <Trash2 className="mr-2 size-4" /> Delete project
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
